if (!customElements.get('sa-meeting')) {
  class SaMeeting extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.alignCardHeights = this.alignCardHeights.bind(this);
      this.startAutoPlay = this.startAutoPlay.bind(this);
      this.stopAutoPlay = this.stopAutoPlay.bind(this);
      
      this.autoplayDuration = 3000;
      this.autoPlayTimer = null;
      this.isAutoPlayEnabled = false;
      this.scrollTimeout = null;
      this.currentIndex = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('.sa-meeting__list');
      this.prevBtns = this.querySelectorAll('.sa-meeting__btn--prev');
      this.nextBtns = this.querySelectorAll('.sa-meeting__btn--next');
      this.progressBar = this.querySelector('.sa-meeting__progress-bar');
      this.indicators = this.querySelectorAll('.sa-meeting__indicator');
      
      if (!this.track) return;

      this.initEvents();
      
      // Initial state
      this.updateControlsVisibility();
      this.updateProgress();

      // Align heights
      this.alignCardHeights();
      // Re-align after a short delay in case of font loading or layout shifts
      setTimeout(this.alignCardHeights, 100);
      
      // Start autoplay
      this.startAutoPlay();
    }

    disconnectedCallback() {
      this.stopAutoPlay();
      if (this.track) {
        this.track.removeEventListener('scroll', this.handleScroll);
      }
      window.removeEventListener('resize', this.handleResize);
    }

    initEvents() {
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      
      this.prevBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.stopAutoPlay();
          this.handlePrev();
          // Resume immediately
          this.startAutoPlay();
        });
      });

      this.nextBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.stopAutoPlay();
          this.handleNext();
          this.startAutoPlay();
        });
      });

      // Pause on hover
      this.addEventListener('mouseenter', this.stopAutoPlay);
      this.addEventListener('mouseleave', this.startAutoPlay);
      
      // Touch events for mobile pause
      this.track.addEventListener('touchstart', this.stopAutoPlay, { passive: true });
      this.track.addEventListener('touchend', () => {
        this.startAutoPlay();
      }, { passive: true });

      window.addEventListener('resize', this.handleResize);
    }

    startAutoPlay() {
      this.isAutoPlayEnabled = true;
      this.runAutoPlayLoop();
    }

    stopAutoPlay() {
      this.isAutoPlayEnabled = false;
      if (this.autoPlayTimer) {
        clearTimeout(this.autoPlayTimer);
        clearInterval(this.autoPlayTimer); // Safe clear both
        this.autoPlayTimer = null;
      }
      
      // Reset mobile animations if stopping
      if (window.innerWidth <= 920) {
        this.resetMobileAnimations();
      }
    }

    runAutoPlayLoop() {
      if (!this.isAutoPlayEnabled) return;
      
      // Clear any existing timer
      if (this.autoPlayTimer) {
        clearTimeout(this.autoPlayTimer);
        clearInterval(this.autoPlayTimer);
      }

      // Desktop: Simple Interval
      if (window.innerWidth > 920) {
        this.autoPlayTimer = setInterval(() => {
          this.handleNext(true);
        }, this.autoplayDuration);
        return;
      }

      // Mobile: Animated Progress
      this.animateMobileIndicator(this.currentIndex);
      
      this.autoPlayTimer = setTimeout(() => {
        this.handleNext(true);
      }, this.autoplayDuration);
    }

    resetMobileAnimations() {
      if (!this.indicators) return;
      this.indicators.forEach(ind => {
        const fill = ind.querySelector('.sa-meeting__indicator-fill');
        if (fill) {
          fill.style.transition = 'none';
          fill.style.width = '0%';
        }
      });
    }

    animateMobileIndicator(index) {
      if (!this.indicators || !this.indicators[index]) return;
      
      // Reset all first
      this.resetMobileAnimations();

      const activeInd = this.indicators[index];
      const fill = activeInd.querySelector('.sa-meeting__indicator-fill');
      
      if (fill) {
        // Force reflow to ensure reset takes effect
        void fill.offsetWidth;
        
        fill.style.transition = `width ${this.autoplayDuration}ms linear`;
        fill.style.width = '100%';
      }
    }

    getCardWidth() {
      const card = this.querySelector('.sa-meeting__card');
      if (!card) return 0;
      const style = window.getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 20;
      return card.offsetWidth + gap;
    }

    handlePrev() {
      const cardWidth = this.getCardWidth();
      if (!cardWidth) return;
      
      const currentScroll = this.track.scrollLeft;
      
      if (currentScroll < 10) {
        this.track.scrollTo({
          left: this.track.scrollWidth,
          behavior: 'auto' // Instant jump
        });
      } else {
        this.track.scrollBy({
          left: -cardWidth,
          behavior: 'smooth'
        });
      }
    }

    handleNext(isAuto = false) {
      const cardWidth = this.getCardWidth();
      if (!cardWidth) return;
      
      const currentScroll = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      
      if (currentScroll >= maxScroll - 10) {
        // Start rewind - INSTANT jump
        this.isRewinding = true;
        this.track.scrollTo({
          left: 0,
          behavior: 'auto' // Instant jump
        });
        // Reset flag immediately for next frame
        requestAnimationFrame(() => {
          this.isRewinding = false;
          this.updateProgress(); // Force update
        });
      } else {
        this.track.scrollBy({
          left: cardWidth,
          behavior: 'smooth'
        });
      }
    }

    handleScroll() {
      if (this.scrollTimeout) {
        cancelAnimationFrame(this.scrollTimeout);
      }
      this.scrollTimeout = requestAnimationFrame(() => {
        this.updateProgress();
      });
    }

    updateProgress() {
      if (!this.track) return;
      
      const scrollLeft = this.track.scrollLeft;
      const scrollWidth = this.track.scrollWidth;
      const clientWidth = this.track.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      
      // Check if rewind is complete (near 0)
      if (this.isRewinding) {
        if (scrollLeft < 10) {
          this.isRewinding = false;
        }
      }

      // 1. Update Desktop Progress Bar
      let progress = 0;
      if (maxScroll > 0) {
        progress = (scrollLeft / maxScroll) * 100;
      }
      progress = Math.min(100, Math.max(0, progress));

      if (this.progressBar) {
        this.progressBar.style.width = `${progress}%`;
      }

      // 2. Update Mobile Indicators & Detect Index Change
      if (this.indicators.length > 0) {
        const cardWidth = this.getCardWidth();
        // Calculate new index
        const index = Math.round(scrollLeft / cardWidth);
        const safeIndex = Math.min(Math.max(0, index), this.indicators.length - 1);

        // Update Active Class
        this.indicators.forEach((ind, i) => {
          if (i === safeIndex) {
            ind.classList.add('is-active');
          } else {
            ind.classList.remove('is-active');
            // Force reset any transition on non-active items
            const fill = ind.querySelector('.sa-meeting__indicator-fill');
            if (fill) {
                fill.style.transition = 'none';
                fill.style.width = '0%';
            }
          }
        });

        // Detect Change
        if (this.currentIndex !== safeIndex) {
          this.currentIndex = safeIndex;
          
          // If auto-play is enabled (and we are on mobile), 
          // restarting the loop will trigger the animation for the NEW index.
          if (this.isAutoPlayEnabled && window.innerWidth <= 920) {
             // If we just rewound (jumped from last to first), we need to ensure 
             // the animation starts fresh on the first dot, without transition lag.
             if (this.isRewinding) {
                this.resetMobileAnimations();
                // Short timeout to allow DOM to clear styles
                setTimeout(() => this.runAutoPlayLoop(), 50);
             } else {
                this.runAutoPlayLoop();
             }
          }
        }
      }
    }

    updateControlsVisibility() {
      if (!this.track) return;
      
      const isScrollable = this.track.scrollWidth > this.track.clientWidth + 2;
      
      if (isScrollable) {
        this.classList.add('is-scrollable');
      } else {
        this.classList.remove('is-scrollable');
      }
    }

    alignCardHeights() {
      const titles = this.querySelectorAll('.sa-meeting__card-title');
      const descs = this.querySelectorAll('.sa-meeting__card-desc');

      // 1. Reset heights to auto to measure natural height
      titles.forEach(el => el.style.height = 'auto');
      descs.forEach(el => el.style.height = 'auto');

      // 2. Find max heights
      let maxTitleHeight = 0;
      let maxDescHeight = 0;

      titles.forEach(el => {
        if (el.offsetHeight > maxTitleHeight) maxTitleHeight = el.offsetHeight;
      });

      descs.forEach(el => {
        if (el.offsetHeight > maxDescHeight) maxDescHeight = el.offsetHeight;
      });

      // 3. Apply max heights (if greater than 0)
      if (maxTitleHeight > 0) {
        titles.forEach(el => el.style.height = `${maxTitleHeight}px`);
      }
      if (maxDescHeight > 0) {
        descs.forEach(el => el.style.height = `${maxDescHeight}px`);
      }
    }

    handleResize() {
      this.updateProgress();
      this.updateControlsVisibility();
      this.alignCardHeights();
      
      // Reset/Restart autoplay logic based on new screen size
      if (this.isAutoPlayEnabled) {
        this.runAutoPlayLoop();
      }
    }
  }

  customElements.define('sa-meeting', SaMeeting);
}
